import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Lock, LogOut, Users, UserCheck, CheckCircle, AlertCircle, Trash2, Phone, Mail, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_LABELS = {
  "gst-registration": "GST Registration",
  "gst-filing": "GST Return Filing",
  "itr-filing": "ITR Filing",
  "tds-filing": "TDS Filing",
  "business-registration": "Business Registration",
  "health-insurance": "Health Insurance",
  "business-loan": "Business Loan Assistance",
  "personal-loan": "Personal Loan Assistance",
  "other": "Other",
};

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  resolved: "bg-green-100 text-green-800",
};

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, resolved: 0 });
  const [filterService, setFilterService] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ token });
      if (filterService) params.append("service", filterService);
      if (filterStatus) params.append("status", filterStatus);
      const res = await axios.get(`${API}/admin/leads?${params}`);
      setLeads(res.data.leads);
      setStats(res.data.stats);
    } catch (err) {
      if (err.response?.status === 401) {
        setToken("");
        localStorage.removeItem("admin_token");
      }
    } finally {
      setLoading(false);
    }
  }, [token, filterService, filterStatus]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setToken(res.data.token);
      localStorage.setItem("admin_token", res.data.token);
    } catch {
      setLoginError("Invalid password");
    }
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("admin_token");
  };

  const updateStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}/status?token=${token}`, { status: newStatus });
      fetchLeads();
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${leadId}?token=${token}`);
      fetchLeads();
    } catch (err) {
      console.error("Failed to delete lead", err);
    }
  };

  if (!token) {
    return (
      <>
        <Helmet>
          <title>Admin Login | WINTRUST SOLUTIONS</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-[70vh] flex items-center justify-center bg-slate-50">
          <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-sm border border-slate-200">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="text-gold" size={24} />
              </div>
              <h1 className="text-2xl font-semibold text-navy font-heading">Admin Login</h1>
            </div>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
                data-testid="admin-password-input"
                autoFocus
              />
              {loginError && <p className="text-red-500 text-sm" data-testid="admin-login-error">{loginError}</p>}
              <Button type="submit" className="w-full h-12 bg-navy hover:bg-navy-900 text-white font-semibold" data-testid="admin-login-btn">
                Login
              </Button>
            </form>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | WINTRUST SOLUTIONS</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="bg-navy py-6">
        <div className="container-custom flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white font-heading" data-testid="admin-dashboard-title">Lead Dashboard</h1>
          <Button variant="outline" className="text-white border-white hover:bg-white hover:text-navy" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </section>

      <section className="bg-slate-50 py-8">
        <div className="container-custom">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Users className="text-navy" size={20} />
                <span className="text-sm text-slate-500">Total Leads</span>
              </div>
              <p className="text-3xl font-bold text-navy" data-testid="stat-total">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg p-5 border border-blue-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="text-blue-600" size={20} />
                <span className="text-sm text-slate-500">New</span>
              </div>
              <p className="text-3xl font-bold text-blue-600" data-testid="stat-new">{stats.new}</p>
            </div>
            <div className="bg-white rounded-lg p-5 border border-yellow-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <UserCheck className="text-yellow-600" size={20} />
                <span className="text-sm text-slate-500">Contacted</span>
              </div>
              <p className="text-3xl font-bold text-yellow-600" data-testid="stat-contacted">{stats.contacted}</p>
            </div>
            <div className="bg-white rounded-lg p-5 border border-green-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-sm text-slate-500">Resolved</span>
              </div>
              <p className="text-3xl font-bold text-green-600" data-testid="stat-resolved">{stats.resolved}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6 items-center" data-testid="admin-filters">
            <Filter size={18} className="text-slate-400" />
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              data-testid="filter-service"
            >
              <option value="">All Services</option>
              {Object.entries(SERVICE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              data-testid="filter-status"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          {/* Leads Table */}
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading leads...</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
              <Users className="mx-auto text-slate-300 mb-3" size={48} />
              <p className="text-slate-500">No leads found</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-x-auto" data-testid="leads-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-4 text-navy font-semibold">Name</th>
                    <th className="text-left p-4 text-navy font-semibold">Contact</th>
                    <th className="text-left p-4 text-navy font-semibold">Service</th>
                    <th className="text-left p-4 text-navy font-semibold">Message</th>
                    <th className="text-left p-4 text-navy font-semibold">Date</th>
                    <th className="text-left p-4 text-navy font-semibold">Status</th>
                    <th className="text-left p-4 text-navy font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${lead.id}`}>
                      <td className="p-4 font-medium text-navy">{lead.name}</td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <a href={`mailto:${lead.email}`} className="text-gold hover:underline flex items-center gap-1 text-xs">
                            <Mail size={12} /> {lead.email}
                          </a>
                          <a href={`tel:${lead.phone}`} className="text-gold hover:underline flex items-center gap-1 text-xs">
                            <Phone size={12} /> {lead.phone}
                          </a>
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">{SERVICE_LABELS[lead.service] || lead.service}</td>
                      <td className="p-4 text-slate-600 max-w-[200px] truncate">{lead.message || "-"}</td>
                      <td className="p-4 text-slate-500 text-xs whitespace-nowrap">
                        {new Date(lead.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td className="p-4">
                        <select
                          value={lead.status}
                          onChange={(e) => updateStatus(lead.id, e.target.value)}
                          className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${STATUS_STYLES[lead.status] || "bg-slate-100 text-slate-600"}`}
                          data-testid={`status-select-${lead.id}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => deleteLead(lead.id)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Delete lead"
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default AdminPage;
